import React from 'react';
import { Form, Formik } from 'formik';
import { getCarePlanTypeConfig } from '~clients/config/carePlanTypes';

import { getErrorMessage } from '~libs/axiosWrapper';
import { useToast } from '~libs/toast';

import { Button, ErrorResponse } from '~components';

import { CarePlan } from '~clients/models';

import { overviewFormSchema } from '~clients/forms/care-plans/overviewForm';

import { useFieldValidationHighlights } from '~clients/hooks/useFieldValidationHighlights';

import { useUpdateCarePlanMutation } from '~clients/actions/carePlans';

import { OverviewFormValues } from './overviewSectionProps';
import PackageScheduleSection from './PackageScheduleSection';
import ParticipantAcknowledgementSection from './ParticipantAcknowledgementSection';
import PlanDetailsSection from './PlanDetailsSection';
import SahBudgetSection from './SahBudgetSection';

interface ComponentProps {
  plan: CarePlan;
}

/**
 * Care plan Overview tab. Plan Details + Acknowledgements share one Formik form saved
 * by the single Save button below the sections; package schedule and SAH budget persist
 * via their own sections. SAH budget shows only for plan types that require it.
 */
const OverviewTab: React.FC<ComponentProps> = ({ plan }) => {
  const { requiresSahBudget } = getCarePlanTypeConfig(plan.plan_type);
  const { showToast } = useToast();
  const [updateCarePlan, { isLoading, error }] = useUpdateCarePlanMutation();
  const { invalidFields, validate } = useFieldValidationHighlights(overviewFormSchema);

  const initialValues: OverviewFormValues = {
    start_date: plan.start_date ?? '',
    end_date: plan.end_date ?? '',
    carer_id: plan.carer_id ?? null,
    is_rights_explained: plan.is_rights_explained,
    is_service_agreement_signed: plan.is_service_agreement_signed,
  };

  const onSubmit = async (values: OverviewFormValues) => {
    try {
      await updateCarePlan({
        id: plan.id,
        start_date: values.start_date,
        end_date: values.end_date || null,
        carer_id: values.carer_id ?? null,
        is_rights_explained: values.is_rights_explained,
        is_service_agreement_signed: values.is_service_agreement_signed,
      }).unwrap();
      showToast('success', 'Care plan updated');
    } catch {
      // Surfaced through the mutation error below.
    }
  };

  return (
    <div className="care-plan-overview" data-cy="care-plan-overview">
      {error !== undefined && <ErrorResponse dataCy="care-plan-overview-error" message={getErrorMessage(error)} />}
      <Formik
        enableReinitialize
        initialValues={initialValues}
        validationSchema={overviewFormSchema}
        validate={validate}
        validateOnMount={false}
        validateOnChange={invalidFields.length > 0}
        validateOnBlur={false}
        onSubmit={onSubmit}
      >
        {({ values, setFieldValue, setFieldTouched, touched, dirty, isSubmitting }) => {
          const sectionProps = { values, setFieldValue, setFieldTouched, touched, invalidFields };

          return (
            <Form>
              <PlanDetailsSection plan={plan} {...sectionProps} />
              <PackageScheduleSection plan={plan} />
              {requiresSahBudget && <SahBudgetSection plan={plan} />}
              <ParticipantAcknowledgementSection plan={plan} {...sectionProps} />
              <div className="care-plan-section-actions">
                <Button
                  type="submit"
                  size="sm"
                  color="success"
                  disabled={!dirty || isSubmitting}
                  loading={isLoading}
                  dataCy="care-plan-overview-save"
                >
                  Save
                </Button>
              </div>
            </Form>
          );
        }}
      </Formik>
    </div>
  );
};

export default OverviewTab;
